import { api } from '../lib/api';

export type Range = [number, number];
export interface Word {
  s: number;
  e: number;
  w: string;
}

/**
 * A piece of another recording, stitched into this one.
 *
 * `at` is a second on THIS recording's original timeline, not a position in
 * the finished video: a clip stays next to the moment it was put beside,
 * whatever gets trimmed around it. `start` and `end` are the in and out
 * points inside the source, which is never touched, so the same recording
 * can be cut differently everywhere it appears.
 */
export interface Clip {
  id: string;
  videoId: string;
  at: number;
  start: number;
  end: number;
}

/** A recording an added clip comes from, as the editor needs to draw it. */
export interface ClipSource {
  videoId: string;
  shareId: string;
  title: string;
  durationSec: number | null;
  posterUrl: string | null;
  url: string | null;
  available: boolean;
}

/** GET /v1/videos/:id/edit */
export interface EditState {
  videoId: string;
  shareId: string;
  title: string;
  kind: 'screen' | 'audio';
  status: string;
  sourceUrl: string;
  sourceDurationSec: number;
  keep: Range[] | null;
  clips: Clip[];
  clipSources: ClipSource[];
  version: number;
  words: Word[] | null;
  transcriptStatus: 'pending' | 'ready' | 'failed' | null;
}

export function fetchEditState(videoId: string): Promise<EditState> {
  return api<EditState>(`/v1/videos/${videoId}/edit`);
}

/** The one recording somebody just picked, with somewhere to scrub it. */
export function fetchClipSource(videoId: string): Promise<ClipSource> {
  return api<ClipSource>(`/v1/videos/${videoId}/clip-source`);
}

export function applyEdit(
  videoId: string,
  body: { keep: Range[] | null; clips?: Clip[]; words?: Word[] },
): Promise<{ ok: boolean; rendering: boolean; version: number }> {
  return api(`/v1/videos/${videoId}/edit`, { method: 'PUT', body });
}
